/**
 * Footing3DView — 3D viewer for an isolated reinforced concrete footing
 * (zapata aislada).
 *
 * Geometry:
 *   - Soil plate (translucent) at grade level.
 *   - Footing slab: Lx × Hf × Ly box, top face at z = -df + Hf.
 *   - Column stub: bc × col_h × hc rising from the footing top to a bit
 *     above the soil to show the column emerging from grade.
 *   - Bottom mesh: nx bars along X and ny bars along Y, sitting at the
 *     bottom of the slab inside the cover.
 *   - Column starter bars: 4 corner bars rising from the slab into the
 *     column (approximation of the dowel detail).
 *
 * Scene axes:
 *   x : along Lx
 *   y : vertical (up)
 *   z : along Ly
 *
 * Coordinate origin: footing centroid in plan, at the BOTTOM of the slab.
 * The top of the soil sits at y = (df) above origin so the soil level matches
 * grade. The column top is at y = df + col_above_grade.
 */

import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid, Bounds } from '@react-three/drei';
import * as THREE from 'three';

const DIAM_INCH_TO_CM: Record<string, number> = {
  '3/8': 0.9525,
  '1/2': 1.27,
  '5/8': 1.5875,
  '3/4': 1.905,
  '1': 2.54,
  '1 3/8': 3.4925,
};

const COLOR_CONCRETE = '#94A3B8';
const COLOR_CONCRETE_EDGE = '#94A3B8';
const COLOR_COLUMN = '#94A3B8';
const COLOR_SOIL = '#7C2D12';
const COLOR_BAR_X = '#F59E0B';
const COLOR_BAR_Y = '#8B5CF6';
const COLOR_DOWEL = '#F59E0B';

interface Props {
  Lx: number;
  Ly: number;
  Hf: number;
  bc: number;
  hc: number;
  df: number;
  cover: number;
  /** Column position from the lower-left corner (cm). Default = centered. */
  x_col?: number;
  y_col?: number;
  diam_x: string;
  n_x: number;
  diam_y: string;
  n_y: number;
  /** Column starter bars (mechones) — anchor with optional 90° hook. */
  diam_col_bar?: string;
  n_col_bars?: number;
  col_bar_hooked?: boolean;
  /** Required hooked development length in cm (drives dowel depth). */
  ldh_required?: number;
}

export function Footing3DView({
  Lx,
  Ly,
  Hf,
  bc,
  hc,
  df,
  cover,
  x_col,
  y_col,
  diam_x,
  n_x,
  diam_y,
  n_y,
  diam_col_bar = '5/8',
  n_col_bars = 4,
  col_bar_hooked = true,
  ldh_required = 35,
}: Props) {
  // Resolved column position (default = centered). In scene coords the
  // footing centroid is at the origin, so the column offset is (x_col − Lx/2)
  // along x and (y_col − Ly/2) along z (Three.js uses y up).
  const xc = x_col ?? Lx / 2;
  const yc = y_col ?? Ly / 2;
  const offX = xc - Lx / 2;            // cm offset of column center along X
  const offZ = yc - Ly / 2;            // cm offset along Z (the y_col plan axis)
  // Column stub height above the bottom-of-slab origin: from top of slab
  // (y = Hf) up through soil to about 0.6 m above grade.
  const col_above_grade = 60.0; // cm
  const col_top = df + col_above_grade;

  // Scale so the longest visible dimension is ~5 scene units.
  const maxDim = Math.max(Lx, Ly, col_top);
  const scale = 5 / Math.max(maxDim, 1);

  const Lx_s = Lx * scale;
  const Ly_s = Ly * scale;
  const Hf_s = Hf * scale;
  const bc_s = bc * scale;
  const hc_s = hc * scale;
  const df_s = df * scale;
  const col_top_s = col_top * scale;

  const d_x = DIAM_INCH_TO_CM[diam_x] ?? 1.5875;
  const d_y = DIAM_INCH_TO_CM[diam_y] ?? 1.5875;

  // Bar centers in cm (origin at slab bottom)
  const yBarX = cover + d_x / 2; // X-dir bars sit lower
  const yBarY = cover + d_x + d_y / 2;

  // Bar positions: X-bars run along X axis, so their length is Lx and their
  // positions are spread across Z (Ly direction).
  const xBarZs = useMemo(() => {
    if (n_x <= 1) return [0];
    const usable = Ly - 2 * (cover + d_x / 2);
    const step = usable / (n_x - 1);
    return Array.from({ length: n_x }, (_, i) => -Ly / 2 + cover + d_x / 2 + i * step);
  }, [n_x, Ly, cover, d_x]);

  // Y-bars run along Z axis, spread across X
  const yBarXs = useMemo(() => {
    if (n_y <= 1) return [0];
    const usable = Lx - 2 * (cover + d_y / 2);
    const step = usable / (n_y - 1);
    return Array.from({ length: n_y }, (_, i) => -Lx / 2 + cover + d_y / 2 + i * step);
  }, [n_y, Lx, cover, d_y]);

  // Bar radii in scene units. Make them slightly chunkier than true scale so
  // they're visible on a slab where Hf can be 60 cm.
  const r_x = Math.max((d_x / 2) * scale, 0.012);
  const r_y = Math.max((d_y / 2) * scale, 0.012);

  // Column dowel positions (corner starter bars rising from inside the slab
  // up into the column). Local to the column center.
  const d_col_bar = DIAM_INCH_TO_CM[diam_col_bar] ?? 1.5875;
  const dowelInset = cover + Math.max(d_x, d_y) + d_col_bar / 2 + 1;
  // 4 corner dowels (minimum). For 6, 8, etc bars: 4 corners + intermediates
  // along the longer side.
  const dowelOffsetsLocal: [number, number][] = [
    [-bc / 2 + dowelInset, -hc / 2 + dowelInset],
    [+bc / 2 - dowelInset, -hc / 2 + dowelInset],
    [-bc / 2 + dowelInset, +hc / 2 - dowelInset],
    [+bc / 2 - dowelInset, +hc / 2 - dowelInset],
  ];
  if (n_col_bars > 4) {
    // Add intermediate bars on the longer side.
    const longSide = bc >= hc ? 'bc' : 'hc';
    const extras = n_col_bars - 4;
    const perSide = Math.ceil(extras / 2);
    for (let i = 0; i < perSide; i++) {
      if (longSide === 'bc') {
        const x = -bc / 2 + dowelInset + ((i + 1) * (bc - 2 * dowelInset)) / (perSide + 1);
        dowelOffsetsLocal.push([x, -hc / 2 + dowelInset]);
        dowelOffsetsLocal.push([x, +hc / 2 - dowelInset]);
      } else {
        const y = -hc / 2 + dowelInset + ((i + 1) * (hc - 2 * dowelInset)) / (perSide + 1);
        dowelOffsetsLocal.push([-bc / 2 + dowelInset, y]);
        dowelOffsetsLocal.push([+bc / 2 - dowelInset, y]);
      }
    }
  }

  // Vertical portion of each dowel: from just above the bottom mesh up to
  // the top of the column.
  const dowelBottomY_cm = cover + Math.max(d_x, d_y) + d_col_bar / 2;
  const dowelTopY_cm = col_top - cover;            // stop a cover below column top
  const dowelHeight_cm = dowelTopY_cm - dowelBottomY_cm;
  const dowelYCenter_cm = (dowelBottomY_cm + dowelTopY_cm) / 2;

  // 90° hook tail length (12·db) — bends OUTWARD (away from column center).
  const hookTail_cm = col_bar_hooked ? Math.max(12 * d_col_bar, 7) : 0;

  return (
    <Canvas
      camera={{ position: [6, 5, 7], fov: 38, near: 0.05, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]}
      gl={{ preserveDrawingBuffer: true }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 6, 5]} intensity={0.95} />
      <directionalLight position={[-4, -2, -3]} intensity={0.28} />

      <Grid
        args={[30, 30]}
        cellSize={0.25}
        cellThickness={0.4}
        cellColor="#475569"
        sectionSize={1}
        sectionThickness={1}
        sectionColor="#334155"
        fadeDistance={20}
        fadeStrength={1.2}
        position={[0, df_s - 0.001, 0]} /* grade level */
        infiniteGrid
      />

      <Bounds fit clip observe margin={1.45}>
        {/* SOIL (around and above footing — translucent) */}
        <mesh position={[0, df_s / 2, 0]}>
          <boxGeometry args={[Lx_s * 1.7, df_s, Ly_s * 1.7]} />
          <meshStandardMaterial color={COLOR_SOIL} transparent opacity={0.10} roughness={1.0} />
        </mesh>

        {/* FOOTING SLAB */}
        <mesh position={[0, Hf_s / 2, 0]}>
          <boxGeometry args={[Lx_s, Hf_s, Ly_s]} />
          <meshStandardMaterial
            color={COLOR_CONCRETE}
            transparent
            opacity={0.42}
            roughness={0.85}
          />
        </mesh>
        <lineSegments position={[0, Hf_s / 2, 0]}>
          <edgesGeometry args={[new THREE.BoxGeometry(Lx_s, Hf_s, Ly_s)]} />
          <lineBasicMaterial color={COLOR_CONCRETE_EDGE} />
        </lineSegments>

        {/* COLUMN STUB — from slab top to above grade, at the column position */}
        <mesh position={[offX * scale, (Hf + (col_top - Hf) / 2) * scale, offZ * scale]}>
          <boxGeometry args={[bc_s, (col_top - Hf) * scale, hc_s]} />
          <meshStandardMaterial color={COLOR_COLUMN} transparent opacity={0.55} roughness={0.85} />
        </mesh>
        <lineSegments position={[offX * scale, (Hf + (col_top - Hf) / 2) * scale, offZ * scale]}>
          <edgesGeometry args={[new THREE.BoxGeometry(bc_s, (col_top - Hf) * scale, hc_s)]} />
          <lineBasicMaterial color={COLOR_CONCRETE_EDGE} />
        </lineSegments>

        {/* BOTTOM MESH — X-bars (along X axis, spread across Z) */}
        {xBarZs.map((zCm, i) => (
          <Cylinder
            key={`bx-${i}`}
            radius={r_x}
            length={Lx_s}
            position={[0, yBarX * scale, zCm * scale]}
            rotation={[0, 0, Math.PI / 2]}
            color={COLOR_BAR_X}
          />
        ))}
        {/* BOTTOM MESH — Y-bars (along Z axis, spread across X) */}
        {yBarXs.map((xCm, i) => (
          <Cylinder
            key={`by-${i}`}
            radius={r_y}
            length={Ly_s}
            position={[xCm * scale, yBarY * scale, 0]}
            rotation={[Math.PI / 2, 0, 0]}
            color={COLOR_BAR_Y}
          />
        ))}

        {/* COLUMN DOWELS — vertical part + 90° hook at bottom (mechones).
            Each dowel goes from inside the footing (just above the bottom
            mesh) up into the column. When col_bar_hooked, a horizontal tail
            of length 12·db extends OUTWARD from the column axis at the
            bottom — this is the standard 90° hook used to develop the bar
            in tension when Hf is shallow. */}
        {dowelOffsetsLocal.map((p, i) => {
          // World position of this dowel = column offset + local offset
          const worldX = (offX + p[0]) * scale;
          const worldZ = (offZ + p[1]) * scale;
          const r_dowel = Math.max((d_col_bar / 2) * scale, 0.014);
          const yCenter_s = dowelYCenter_cm * scale;
          const len_s = dowelHeight_cm * scale;
          // Hook tail direction (outward from column center, in the X-Z plane)
          const dx = p[0];
          const dz = p[1];
          const norm = Math.hypot(dx, dz) || 1;
          const ux = dx / norm;
          const uz = dz / norm;
          // Hook positioned at the bottom of the vertical bar, extending
          // horizontally outward. Its midpoint is at distance hookTail/2
          // from the vertical bar.
          const hookY_s = dowelBottomY_cm * scale + r_dowel * 0.6;
          const hookCenterX_s = worldX + ux * (hookTail_cm / 2) * scale;
          const hookCenterZ_s = worldZ + uz * (hookTail_cm / 2) * scale;
          // Rotation: cylinder default axis is +y. Rotate it so its axis
          // points along (ux, 0, uz). Euler around y-axis by angle.
          const angle = Math.atan2(uz, ux);
          return (
            <group key={`dow-${i}`}>
              {/* Vertical part */}
              <Cylinder
                radius={r_dowel}
                length={len_s}
                position={[worldX, yCenter_s, worldZ]}
                rotation={[0, 0, 0]}
                color={COLOR_DOWEL}
              />
              {/* 90° hook tail */}
              {col_bar_hooked && hookTail_cm > 0 && (
                <mesh
                  position={[hookCenterX_s, hookY_s, hookCenterZ_s]}
                  rotation={[0, -angle, Math.PI / 2]}
                >
                  <cylinderGeometry args={[r_dowel, r_dowel, hookTail_cm * scale, 14]} />
                  <meshStandardMaterial color={COLOR_DOWEL} metalness={0.5} roughness={0.4} />
                </mesh>
              )}
            </group>
          );
        })}
      </Bounds>

      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={['#22C55E', '#F59E0B', '#3B82F6']} labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}

function Cylinder({
  radius,
  length,
  position,
  rotation,
  color,
}: {
  radius: number;
  length: number;
  position: [number, number, number];
  rotation: [number, number, number];
  color: string;
}) {
  const geom = useMemo(
    () => new THREE.CylinderGeometry(radius, radius, length, 14),
    [radius, length],
  );
  return (
    <mesh position={position} rotation={rotation} geometry={geom}>
      <meshStandardMaterial color={color} metalness={0.45} roughness={0.4} />
    </mesh>
  );
}
